import { supabase } from "@/integrations/supabase/client";
import { uploadPatientFile, callExtractText, updateDocumentAnalysis } from "@/services/docread";
import { extractPdfTextBrowser } from "@/services/pdf-extract";
import { extractXlsxTextBrowser } from "@/services/xlsx-extract";
import { saveLabResultsFromAnalysis } from "@/services/docread.dal";
import { toast } from "sonner";

export type UploadingResult = {
  documentId: string;
  fileName: string;
  fileType: string;
  extractedText: string;
  lab_results: string;
  inserted_tests: number;
  analysis: {
    summary: string;
    insights: any[];
    risk_level: string;
  };
};

/**
 * Análise assíncrona em background
 */
async function analyzeDocumentInBackground(
  documentId: string,
  patientId: string,
  text: string,
  patientContext: {
    name: string;
    cpf: string;
    age: number | null;
    sex: string | null;
  }
) {
  try {
    console.log('Starting background analysis for document:', documentId);

    const { data: aiAnalysis, error: aiError } = await supabase.functions.invoke(
      'validate-clinical-data',
      {
        body: {
          action: 'propose',
          editedLabText: text,
          patientContext
        }
      }
    );

    if (aiError) {
      console.error('AI analysis error:', aiError);

      // Tratamento específico para timeout
      if (aiError.message?.includes('504') || aiError.message?.includes('timeout')) {
        throw new Error('Documento muito extenso. Divida em uploads menores (máx. 10 páginas por arquivo).');
      }

      throw new Error(aiError.message || 'Erro na análise com IA');
    }

    if (!aiAnalysis?.structuredJson) {
      throw new Error('Resposta inválida da IA');
    }

    console.log('AI analysis completed, updating document');

    // Atualizar documento com análise
    await updateDocumentAnalysis(documentId, {
      extracted_text: text,
      ai_summary: aiAnalysis.structuredJson.analysis?.summary,
      ai_insights: aiAnalysis.structuredJson,
      ai_risk_level: aiAnalysis.structuredJson.analysis?.risk_level,
      lab_results: aiAnalysis.structuredJson.lab_results,
      has_lab_data: (aiAnalysis.structuredJson.structured?.tests?.length || 0) > 0,
      status: 'processed'
    });

    // Salvar dados estruturados
    if (aiAnalysis.structuredJson.structured?.tests) {
      const { inserted } = await saveLabResultsFromAnalysis(
        patientId,
        documentId,
        aiAnalysis.structuredJson
      );
      console.log(`Inserted ${inserted} lab results`);
    }

    // Marcar como concluído
    await supabase
      .from('documents_onboarding')
      .update({ processing_status: 'completed' })
      .eq('id', documentId);

    console.log('Background analysis completed successfully');
  } catch (error: any) {
    console.error('Background analysis failed:', error);

    // Mensagem específica para timeout
    const isTimeout = error.message?.includes('timeout') ||
      error.message?.includes('504') ||
      error.message?.includes('muito extenso');

    const errorMessage = isTimeout
      ? 'Documento muito grande. Divida em múltiplos arquivos menores (máx. 10 páginas cada) para melhor processamento.'
      : `Erro na análise: ${error.message}`;

    // Marcar como falha
    await supabase
      .from('documents_onboarding')
      .update({
        processing_status: 'failed',
        ai_summary: errorMessage
      })
      .eq('id', documentId);
  }
}

/**
 * UPLOADING - Processo unificado de upload, extração e análise com IA
 * Usado por: Onboarding, PatientDocumentUpload, ExpressUploadArea
 * 
 * Fluxo padronizado:
 * 1. Upload para patient-docs storage
 * 2. Criar registro em documents_onboarding
 * 3. Extrair texto (PDF/XLSX browser, imagens edge)
 * 4. Marcar como 'analyzing' e iniciar análise assíncrona
 * 5. Retornar imediatamente (2-5s)
 * 
 * Background (não bloqueante):
 * 6. Analisar com validate-clinical-data (action: propose)
 * 7. Salvar dados estruturados em lab_results_onboarding
 * 8. Atualizar documento com ai_insights completo + has_lab_data
 * 9. Marcar como 'completed' ou 'failed'
 */
export async function uploading(
  patientId: string,
  appointmentId: string,
  file: File,
  patientContext?: {
    name: string;
    cpf: string;
    age: number | null;
    sex: string | null;
  }
): Promise<UploadingResult> {

  const ext = file.name.split(".").pop()!.toLowerCase();
  const validTypes = ["pdf", "jpg", "jpeg", "png", "docx", "xlsx", "xls"];

  if (!validTypes.includes(ext)) {
    throw new Error("Tipo de arquivo não suportado. Use PDF, JPG, PNG, DOCX, XLSX ou XLS.");
  }

  // 1. Upload
  toast.info("Enviando arquivo...");
  const { storagePath, documentId } = await uploadPatientFile(
    patientId,
    appointmentId,
    file
  );

  if (!documentId) throw new Error("Falha ao criar registro do documento");

  // 2. Extrair texto
  toast.info("Extraindo texto do documento...");
  let text = "";
  if (ext === "pdf") {
    text = await extractPdfTextBrowser(file);
  } else if (ext === "xlsx" || ext === "xls") {
    text = await extractXlsxTextBrowser(file);
  } else {
    text = await callExtractText(storagePath, ext, file);
  }

  if (!text?.trim()) {
    throw new Error("Não foi possível extrair texto do documento");
  }

  // 3. Marcar como "em processamento" e iniciar análise em background
  await supabase
    .from('documents_onboarding')
    .update({ processing_status: 'analyzing' })
    .eq('id', documentId);

  // Iniciar análise assíncrona (não aguardar)
  analyzeDocumentInBackground(
    documentId,
    patientId,
    text,
    patientContext || {
      name: 'Paciente',
      cpf: '',
      age: null,
      sex: null
    }
  ).catch(err => console.error('Background analysis failed:', err));

  toast.success("Documento enviado! Análise em andamento...");

  return {
    documentId,
    fileName: file.name,
    fileType: ext,
    extractedText: text,
    lab_results: '',
    inserted_tests: 0,
    analysis: {
      summary: 'Análise em andamento...',
      insights: [],
      risk_level: 'baixo'
    }
  };
}
